import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Phone, Mail, Eye, Loader2, AlertTriangle, X, Settings,
  MessageCircle, Calendar, User, FileText, Upload, Download,
  ToggleLeft, ToggleRight, ChevronLeft, Building2, Clock,
  Edit3, Save, Trash2, Shield, Users, Briefcase, StickyNote
} from 'lucide-react';
import apiClient from '../utils/apiClient';

const formatCurrency = (value) => {
  if (Math.abs(value) >= 1_000_000) {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 1, notation: 'compact' }).format(value);
  }
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value);
};

const calcAntiguedad = (fecha) => {
  if (!fecha) return null;
  const inicio = new Date(fecha);
  const hoy = new Date();
  const meses = (hoy.getFullYear() - inicio.getFullYear()) * 12 + (hoy.getMonth() - inicio.getMonth());
  if (meses < 1) return 'Menos de 1 mes';
  if (meses < 12) return `${meses} mes${meses > 1 ? 'es' : ''}`;
  const anios = Math.floor(meses / 12);
  const resto = meses % 12;
  return `${anios} año${anios > 1 ? 's' : ''}${resto ? ` y ${resto} mes${resto > 1 ? 'es' : ''}` : ''}`;
};

// ===============================================================
// SECCION FACTURACION (dentro del detalle)
// ===============================================================
const FacturacionSection = ({ config }) => {
  const [registros, setRegistros] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    apiClient.get(`/clientes-config/${config.id}/factura/datos`)
      .then(res => setRegistros(res.data?.length || 0))
      .catch(() => setRegistros(0));
  }, [config.id]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const res = await apiClient.post(`/clientes-config/${config.id}/factura/cargar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg({ type: 'ok', text: res.data.message });
      setRegistros(res.data.registros || 0);
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Error subiendo Excel' });
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 mb-6">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
        <FileText size={16} /> Tabla de Facturacion
      </h3>

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
          <FileText size={20} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {registros > 0 ? `${registros} registros cargados` : 'Sin datos de facturacion'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Tabla: {config.tabla_factura} | Campo tarifa: {config.campo_tarifa}
          </p>
        </div>
      </div>

      {msg && (
        <div className={`mb-3 p-2.5 rounded-xl text-sm ${msg.type === 'ok' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {msg.text}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <a href={`https://app.legalpp.com.co/legalplus/clientes-config/${config.id}/factura/plantilla`}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-xl text-sm font-medium hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors">
          <Download size={16} /> {registros > 0 ? 'Descargar Excel actual' : 'Descargar plantilla'}
        </a>
        <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-dashed border-gray-300 dark:border-gray-600'}`}>
          <Upload size={16} />
          {uploading ? 'Subiendo...' : 'Cargar Excel'}
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      {registros === 0 && (
        <p className="text-xs text-gray-400 mt-3">
          Descarga la plantilla, llena los IDs, tarifas y honorarios de esta aseguradora, y sube el Excel para activar la facturacion.
        </p>
      )}
    </div>
  );
};

// ===============================================================
// VISTA DETALLE DE ASEGURADORA
// ===============================================================
const ClienteDetalle = ({ config, stats, onBack, onUpdated, isAdmin }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...config });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const navigate = useNavigate();

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await apiClient.put(`/clientes-config/${config.id}`, {
        nombre_display: form.nombre_display,
        contacto_nombre: form.contacto_nombre,
        contacto_email: form.contacto_email,
        fecha_inicio_contrato: form.fecha_inicio_contrato || null,
        responsable_nombre: form.responsable_nombre,
        responsable_cargo: form.responsable_cargo,
        responsable_telefono: form.responsable_telefono,
        notas: form.notas,
        activo: form.activo,
      });
      setEditing(false);
      setMsg({ type: 'ok', text: 'Guardado correctamente' });
      onUpdated();
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Error guardando' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async () => {
    const nuevoEstado = !form.activo;
    try {
      await apiClient.put(`/clientes-config/${config.id}`, { activo: nuevoEstado });
      setForm(prev => ({ ...prev, activo: nuevoEstado }));
      onUpdated();
      setMsg({ type: 'ok', text: nuevoEstado ? 'Cliente activado' : 'Cliente desactivado' });
    } catch (err) {
      setMsg({ type: 'err', text: 'Error cambiando estado' });
    }
  };

  const handleDeleteCliente = async () => {
    if (!window.confirm(`¿Eliminar "${config.nombre_display}" y todos sus casos asociados? Esta acción no se puede deshacer.`)) return;
    try {
      await apiClient.delete(`/clientes/${encodeURIComponent(config.nombre_display)}`);
      setMsg({ type: 'ok', text: 'Cliente eliminado correctamente' });
      setTimeout(() => onBack(), 1000);
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Error eliminando cliente' });
    }
  };

  const handleUploadContrato = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      await apiClient.post(`/clientes-config/${config.id}/contrato`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg({ type: 'ok', text: 'Contrato subido correctamente' });
      setForm(prev => ({ ...prev, contrato_url: `dummy_${Date.now()}` }));
      onUpdated();
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Error subiendo contrato' });
    } finally {
      setUploading(false);
    }
  };

  const antiguedad = calcAntiguedad(form.fecha_inicio_contrato);

  return (
    <div className="p-4 md:p-8 bg-slate-100 dark:bg-gray-950 min-h-screen overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          <ChevronLeft size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{config.nombre_display}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">slug: {config.slug} | Creado: {config.fecha_creacion}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleToggleActivo}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${form.activo ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
            {form.activo ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            {form.activo ? 'Activo' : 'Inactivo'}
          </button>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors">
              <Edit3 size={16} /> Editar
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50">
              <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
          )}
          {isAdmin && (
            <button onClick={handleDeleteCliente} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors">
              <Trash2 size={16} /> Eliminar
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-xl text-sm font-medium ${msg.type === 'ok' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {msg.text}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Casos</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.cases || 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Facturado</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.totalValue || '$0'}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Antiguedad</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{antiguedad || 'Sin fecha'}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Telegram</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{config.telegram_chat_id ? 'Vinculado' : 'Sin vincular'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Informacion general */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Building2 size={16} /> Informacion General
          </h3>
          <div className="space-y-4">
            <Field label="Nombre" icon={<Building2 size={14} />} value={form.nombre_display} editing={editing}
              onChange={v => setForm(p => ({ ...p, nombre_display: v }))} />
            <Field label="Contacto" icon={<User size={14} />} value={form.contacto_nombre} editing={editing}
              onChange={v => setForm(p => ({ ...p, contacto_nombre: v }))} placeholder="Nombre del contacto" />
            <Field label="Email" icon={<Mail size={14} />} value={form.contacto_email} editing={editing}
              onChange={v => setForm(p => ({ ...p, contacto_email: v }))} placeholder="email@empresa.com" />
            <Field label="Fecha inicio contrato" icon={<Calendar size={14} />} value={form.fecha_inicio_contrato} editing={editing}
              onChange={v => setForm(p => ({ ...p, fecha_inicio_contrato: v }))} type="date" />
          </div>
        </div>

        {/* Responsable y equipo */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Users size={16} /> Responsable del Cliente
          </h3>
          <div className="space-y-4">
            <Field label="Nombre responsable" icon={<Shield size={14} />} value={form.responsable_nombre} editing={editing}
              onChange={v => setForm(p => ({ ...p, responsable_nombre: v }))} placeholder="Nombre del responsable interno" />
            <Field label="Cargo" icon={<Briefcase size={14} />} value={form.responsable_cargo} editing={editing}
              onChange={v => setForm(p => ({ ...p, responsable_cargo: v }))} placeholder="Director comercial, etc." />
            <Field label="Telefono" icon={<Phone size={14} />} value={form.responsable_telefono} editing={editing}
              onChange={v => setForm(p => ({ ...p, responsable_telefono: v }))} placeholder="3001234567" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Telegram */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
            <MessageCircle size={16} /> Telegram
          </h3>
          {config.telegram_chat_id ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <MessageCircle size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Grupo vinculado</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{config.telegram_chat_id}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <MessageCircle size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Sin grupo vinculado</p>
              <p className="text-xs text-gray-400 mt-1">Agrega <strong>@Omnia_nuevo</strong> al grupo de Telegram y envia cualquier mensaje. El bot vinculara este grupo automaticamente.</p>
            </div>
          )}
        </div>

        {/* Contrato */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
            <FileText size={16} /> Contrato
          </h3>
          <div className="flex flex-col items-center gap-3">
            {form.contrato_url ? (
              <div className="flex items-center gap-3 w-full">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <FileText size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Contrato cargado</p>
                </div>
                <a href={`https://app.legalpp.com.co/legalplus/clientes-config/${config.id}/contrato/descargar`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-xl text-sm hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors">
                  <Download size={14} /> Descargar
                </a>
              </div>
            ) : (
              <div className="text-center py-2">
                <FileText size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Sin contrato</p>
              </div>
            )}
            <label className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-dashed border-gray-300 dark:border-gray-600'}`}>
              <Upload size={16} />
              {uploading ? 'Subiendo...' : form.contrato_url ? 'Reemplazar contrato' : 'Subir contrato'}
              <input type="file" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png" className="hidden" onChange={handleUploadContrato} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>

      {/* Facturacion */}
      <FacturacionSection config={config} />

      {/* Notas */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 mb-6">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
          <StickyNote size={16} /> Notas
        </h3>
        {editing ? (
          <textarea value={form.notas || ''} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
            rows={4} placeholder="Notas internas sobre este cliente..."
            className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 resize-none" />
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{form.notas || 'Sin notas'}</p>
        )}
      </div>

      {/* Acciones */}
      <div className="flex gap-3 pb-8">
        <button onClick={() => navigate(`/casos?aseguradora=${config.nombre_display}`)}
          className="flex items-center gap-2 px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 active:scale-95">
          <Eye size={18} /> Ver expedientes
        </button>
        <button onClick={onBack}
          className="flex items-center gap-2 px-6 py-3.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
          <ChevronLeft size={18} /> Volver a clientes
        </button>
      </div>
    </div>
  );
};

// Campo editable/lectura
const Field = ({ label, icon, value, editing, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">{icon} {label}</label>
    {editing ? (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 text-sm" />
    ) : (
      <p className="text-sm font-medium text-gray-900 dark:text-white">{value || <span className="text-gray-400">-</span>}</p>
    )}
  </div>
);

// ===============================================================
// MODAL CREAR ASEGURADORA
// ===============================================================
const NuevoClienteModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ nombre_display: '', contacto_nombre: '', contacto_email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!form.nombre_display.trim()) { setError('El nombre es obligatorio'); return; }
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/clientes-config', {
        nombre_display: form.nombre_display.trim(),
        contacto_nombre: form.contacto_nombre || null,
        contacto_email: form.contacto_email || null,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error creando aseguradora');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Nueva Aseguradora</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
            <input placeholder="Ej: NUEVA ASEGURADORA" value={form.nombre_display}
              onChange={e => setForm(p => ({ ...p, nombre_display: e.target.value }))}
              className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contacto</label>
              <input placeholder="Nombre" value={form.contacto_nombre}
                onChange={e => setForm(p => ({ ...p, contacto_nombre: e.target.value }))}
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input placeholder="email@empresa.com" value={form.contacto_email}
                onChange={e => setForm(p => ({ ...p, contacto_email: e.target.value }))}
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600" />
            </div>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800/50">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <MessageCircle size={14} className="inline mr-1" />
              Las tablas se crearan automaticamente. Para vincular Telegram, agrega @Omnia_nuevo al grupo y luego edita el cliente para pegar el Chat ID.
            </p>
          </div>
        </div>
        {error && <p className="text-red-500 text-sm mt-3 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">{error}</p>}
        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} disabled={loading || !form.nombre_display.trim()}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition-all">
            {loading ? 'Creando...' : 'Crear Aseguradora'}
          </button>
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-xl">Cancelar</button>
        </div>
      </div>
    </div>
  );
};

// ===============================================================
// TARJETA DE ASEGURADORA (LISTA)
// ===============================================================
const ClientCard = ({ client, config, onSelect }) => {
  // El estado autoritativo viene del resumen (client.status); config.activo es
  // fallback. Antes leia solo config?.activo -> inactivos sin config salian "Activo".
  const isActive = client?.status ? client.status !== 'Inactivo' : (config?.activo !== false);
  const hasTelegram = !!config?.telegram_chat_id;
  const antiguedad = calcAntiguedad(config?.fecha_inicio_contrato);

  return (
    <div onClick={() => onSelect(config)} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 cursor-pointer transition-all hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 hover:-translate-y-0.5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold text-xl rounded-xl flex items-center justify-center shadow">
            {client.name.charAt(0)}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{client.name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{config?.contacto_nombre || 'Sin contacto'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasTelegram && (
            <span className="w-2 h-2 bg-green-500 rounded-full" title="Telegram vinculado" />
          )}
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
            {isActive ? 'Activo' : 'Inactivo'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-gray-900 dark:text-white">{client.cases}</p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Casos</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 text-center">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{client.totalValue}</p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Facturado</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 text-center">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{antiguedad || '-'}</p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Antiguedad</p>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <span className="flex items-center gap-1"><Mail size={12} /> {config?.contacto_email || '-'}</span>
        <span className="text-blue-500 font-medium">Ver detalle →</span>
      </div>
    </div>
  );
};

// ===============================================================
// COMPONENTE PRINCIPAL
// ===============================================================
const Clientes = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [clientesConfig, setClientesConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedStats, setSelectedStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // F-Perf.1: el resumen por cliente (casos, total honorarios, estado) se
      // agrega server-side. Antes se bajaban /casos (~3.5MB) + /factura (~2.3MB)
      // y se sumaba en el navegador; ahora son ~1KB en un solo endpoint.
      const [resumenRes, configRes] = await Promise.all([
        apiClient.get('/clientes-config/resumen'),
        apiClient.get('/clientes-config'),
      ]);

      const normalizeNombre = (nombre) => {
        if (!nombre) return nombre;
        const lower = nombre.toLowerCase().trim();
        if (lower.includes('asis') && lower.includes('prex')) return 'Assisprex';
        return nombre;
      };
      const configData = configRes.data.map(c => ({
        ...c,
        nombre_display: normalizeNombre(c.nombre_display),
      }));
      setClientesConfig(configData);

      const agg = resumenRes.data.map(c => ({
        name: c.name,
        contact: c.contact || '',
        email: c.email || '',
        cases: c.cases,
        totalValue: formatCurrency(c.totalValue || 0),
        status: c.status,
      }));
      setClients(agg);
    } catch (err) {
      console.error(err);
      setError('No se pudieron cargar los datos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getConfig = (name) => clientesConfig.find(c => c.nombre_display.toUpperCase() === name.toUpperCase());
  const getStats = (name) => clients.find(c => c.name.toUpperCase() === name.toUpperCase());

  const handleSelect = (config) => {
    if (config?.id) {
      setSelectedConfig(config);
      setSelectedStats(getStats(config.nombre_display));
    }
  };

  if (selectedConfig) {
    return (
      <ClienteDetalle
        config={selectedConfig}
        stats={selectedStats}
        onBack={() => { setSelectedConfig(null); fetchData(); }}
        onUpdated={fetchData}
        isAdmin={(user?.rol || user?.role || '').toLowerCase() === 'administrador'}
      />
    );
  }

  return (
    <div className="p-4 md:p-8 bg-slate-100 dark:bg-gray-950 min-h-screen overflow-y-auto">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Aseguradoras</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{clientesConfig.length} cliente{clientesConfig.length !== 1 ? 's' : ''} registrado{clientesConfig.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setModalOpen(true)}
          className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95">
          <Plus size={20} /> Nueva Aseguradora
        </button>
      </div>

      {loading && (
        <div className="flex flex-col justify-center items-center h-64">
          <Loader2 size={48} className="animate-spin text-blue-500 mb-4" />
          <span className="text-lg text-gray-500 dark:text-gray-400">Cargando clientes...</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center justify-center h-64 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-6 rounded-2xl border border-red-200 dark:border-red-900/30">
          <AlertTriangle size={24} className="mr-3" />
          <span className="text-lg font-medium">{error}</span>
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 pb-12">
          {clients.map(client => (
            <ClientCard key={client.name} client={client} config={getConfig(client.name)} onSelect={handleSelect} />
          ))}
        </div>
      )}

      {modalOpen && <NuevoClienteModal onClose={() => setModalOpen(false)} onCreated={fetchData} />}
    </div>
  );
};

export default Clientes;
